/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import moment from 'moment';

// Component imports
import { Button, Checkbox, Col, Divider, Form, Input, List, Row, Space, Switch, Tree, Typography } from 'antd';
import { CloseCircleFilled } from '@ant-design/icons';
import SideModal from '../Layout/SideModal';
import { ArrowDownIcon } from '../Icons/ArrowDownIcon';
import MultiDropdown from '../Forms/Fields/MultiDropdown';
import BotAvatar from '../UI/BotAvatar';

// Misc imports
import { isEmail } from '../../utils/is/isEmail';
import { ANALYTICS_SECTIONS, DEFAULT_DATETIME_FORMAT } from '../../constants/main';
import { getCurrentQueryState } from '../../store/utils/getCurrentQueryState';
import { useFetchOrganizationQuery } from '../../store/organization/organizationApi';
import { useCreateScheduledReportMutation, useUpdateScheduledReportMutation } from '../../store/scheduledReports/scheduledReportsApi';
import useTheme from '../../utils/hooks/useTheme';
import { isEmptyObject } from '../../utils/is/isEmptyObject';

// Constant declarations
const ANALYTICS_SECTIONS_KEYS = Object.keys(ANALYTICS_SECTIONS).reduce((acc, key) => {
  acc[key] = key;
  return acc;
}, {});

const ScheduledReportFormModal = ({ selectedItem = undefined, setSelectedItem }) => {
  const { orgId } = useParams();
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const initialValues = useMemo(() => {
    return selectedItem && selectedItem.id ? {
      enabled: selectedItem.enabled,
      name: selectedItem.name,
      description: selectedItem.description ?? '',
      tenants: selectedItem.tenants,
      recipients: selectedItem.recipients,
      recipientInput: '',
      monthly: selectedItem.monthly,
      weekly: selectedItem.weekly,
      sections: selectedItem.sections,
      createdBy: selectedItem.createdBy,
      createdAt: selectedItem.createdAt,
      modifiedBy: selectedItem.modifiedBy,
      modifiedAt: selectedItem.modifiedAt
    } : {
      enabled: true,
      name: '',
      description: '',
      tenants: [],
      recipients: [],
      recipientInput: '',
      monthly: false,
      weekly: false,
      sections: []
    };
  }, [selectedItem]);
  const { formProps } = useTheme();

  const { data: bots } = getCurrentQueryState('fetchBots', { orgId });
  const { data: organization } = useFetchOrganizationQuery({ orgId });
  const [createScheduledReport, { isLoading: isCreatingScheduledReport }] = useCreateScheduledReportMutation();
  const [updateScheduledReport, { isLoading: isUpdatingScheduledReport }] = useUpdateScheduledReportMutation();

  // Reload the form when selectedItem changes
  useEffect(() => {
    form.resetFields();
  }, [selectedItem]);

  const onAddRecipients = () => {
    const formattedInput = form.getFieldValue('recipientInput').split(/,\s*/g).map(email => email.trim()).filter(email => isEmail(email));

    const emails = [
      ...formattedInput,
      ...form.getFieldValue('recipients')
    ];

    const uniqueEmails = [...new Set(emails)];

    form.setFieldsValue({
      recipients: uniqueEmails,
      recipientInput: ''
    });
  };

  const onSubmit = async (values) => {
    let valuesToSubmit = {
      ...values,
      sections: form.getFieldValue('sections').filter((section) => section !== 'sections'),
      tenants: bots.items.filter(bot => form.getFieldValue('tenants').includes(bot.id)).map((bot)=> bot.id),
      recipients: form.getFieldValue('recipients'),
      timezone: organization?.timezone
    };
    delete valuesToSubmit.recipientInput;

    if (!valuesToSubmit.description.trim()) {
      valuesToSubmit.description = null;
    }
    if (!valuesToSubmit.tenants.length) {
      delete valuesToSubmit.tenants;
    }

    let isSaveSuccessful;
    if (selectedItem.id) {
      isSaveSuccessful = await updateScheduledReport({ orgId, reportId: selectedItem.id, data: valuesToSubmit }).unwrap();
    } else {
      isSaveSuccessful = await createScheduledReport({ orgId, data: valuesToSubmit }).unwrap();
    }

    if (isSaveSuccessful) {
      setSelectedItem(undefined);
    }
  };

  return (
    <SideModal
      open={!!selectedItem}
      title={!isEmptyObject(selectedItem) ? t('Edit Report ') : t('Create New Report')}
      onClose={() => setSelectedItem(undefined)}
      footer={
        <Row justify="space-between">
          <Col>
            {
              (form.getFieldValue('createdAt') && form.getFieldValue('modifiedAt')) ? (
                <Col>
                  <Typography.Paragraph type="secondary" className="mb-0 fs-sm" strong>
                    {t('Created')}: {form.getFieldValue('createdBy')}, {moment.tz(form.getFieldValue('createdAt'), organization?.timezone).format(DEFAULT_DATETIME_FORMAT)}
                  </Typography.Paragraph>

                  <Typography.Paragraph type="secondary" className="mb-0 fs-sm" strong>
                    {t('Last modified')}:  {form.getFieldValue('modifiedBy') ?? form.getFieldValue('createdBy')}, {moment.tz(form.getFieldValue('modifiedAt'), organization?.timezone).format(DEFAULT_DATETIME_FORMAT)}
                  </Typography.Paragraph>
                </Col>
              ) : null
            }
          </Col>

          <Col>
            <Space>
              <Button
                size="large"
                onClick={() => setSelectedItem(undefined)}
                disabled={isCreatingScheduledReport || isUpdatingScheduledReport}
              >
                {t('Cancel')}
              </Button>

              <Button
                type="primary"
                size="large"
                onClick={() => form.submit()}
                loading={isCreatingScheduledReport || isUpdatingScheduledReport}
              >
                {isEmptyObject(selectedItem) ? t('Create Report') : t('Save Changes')}
              </Button>
            </Space>
          </Col>
        </Row>
      }
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onSubmit}
        initialValues={initialValues}
      >
        <Row gutter={16} className={formProps.flexDirection}>
          <Col xs={formProps.mainColSpan} className="mb-5">
            <Typography.Paragraph className="mb-0">
              {t('Report Details')}
            </Typography.Paragraph>
            <Typography.Paragraph className="fs-sm mb-0" type="secondary" strong>
              {t('Describe your report')}
            </Typography.Paragraph>
          </Col>

          <Col xs={formProps.secondaryColSpan}>
            <Form.Item
              shouldUpdate
              noStyle
            >
              {() => (
                <Form.Item
                  name="enabled"
                  label={t('Enable Report')}
                  valuePropName="checked"
                  hidden={!form.getFieldValue('createdAt')}
                >
                  <Switch />
                </Form.Item>
              )}
            </Form.Item>

            <Form.Item
              name="name"
              label={t('Report Group Name')}
              validateTrigger={['onChange', 'onBlur']}
              rules={[{
                required: true,
                type: 'string',
                whitespace: true,
                message: t('Please enter a name for this report')
              }]}
            >
              <Input
                placeholder={t('Set the group name (20 characters max)')}
                maxLength={20}
                showCount
              />
            </Form.Item>

            <Form.Item
              name="description"
              label={t('Description')}
            >
              <Input.TextArea
                autoSize={{ maxRows: 4, minRows: 4 }}
                rows={4}
                placeholder={t('Set the report description (100 characters max)')}
                maxLength={100}
                showCount
              />
            </Form.Item>
          </Col>
        </Row>

        <Divider />

        <Row wrap gutter={16}>
          <Col xs={formProps.mainColSpan} className="mb-5">
            <Typography.Paragraph className="mb-0">
              {t('Agents')}
            </Typography.Paragraph>
            <Typography.Paragraph className="fs-sm mb-0" type="secondary" strong>
              {t('Select the agent(s) to be included in the report')}
            </Typography.Paragraph>
          </Col>

          <Col xs={formProps.secondaryColSpan}>
            <Form.Item
              name="tenants"
              label={t('Agents in this Report')}
              className="mb-0"
              rules={[
                {
                  validator: async (_rule, value) => {
                    if (!value.length) {
                      throw new Error(t('Please select at least one agent'));
                    }
                  }
                }
              ]}
            >
              <MultiDropdown
                size="large"
                placeholder={t('Select agent(s)')}
                allowClear
                autoClearSearchValue={false}
                hideExtraFromDropdown
                options={(bots?.items || []).map(bot => ({
                  label: <BotAvatar bot={bot} wrapBotName maxNameCharacters={28} className="d-inline-block w-auto" />,
                  value: bot.id,
                  searchValue: bot.displayName
                }))}
              />
            </Form.Item>
          </Col>
        </Row>

        <Divider />

        <Row wrap gutter={16}>
          <Col xs={formProps.mainColSpan} className="mb-5">
            <Typography.Paragraph className="mb-0">
              {t('Frequency')}
            </Typography.Paragraph>
            <Typography.Paragraph className="fs-sm mb-0" type="secondary" strong>
              {t('Choose how often the report will be sent')}
            </Typography.Paragraph>
          </Col>

          <Col xs={formProps.secondaryColSpan}>
            <Form.Item
              name="weekly"
              valuePropName="checked"
              className="mb-0"
            >
              <Checkbox>{t('Weekly')}</Checkbox>
            </Form.Item>

            <Form.Item
              name="monthly"
              valuePropName="checked"
            >
              <Checkbox>{t('Monthly')}</Checkbox>
            </Form.Item>
          </Col>
        </Row>

        <Divider />

        <Row wrap gutter={16}>
          <Col xs={formProps.mainColSpan} className="mb-5">
            <Typography.Paragraph className="mb-0">
              {t('Content')}
            </Typography.Paragraph>
            <Typography.Paragraph className="fs-sm mb-0" type="secondary" strong>
              {t('Choose Analytics sections to be included in the report')}
            </Typography.Paragraph>
          </Col>

          <Col xs={formProps.secondaryColSpan}>
            <Form.Item
              name="sections"
              valuePropName="checkedKeys"
              trigger="onCheck"
              className="mb-0"
              validateTrigger={['onCheck']}
              rules={[{
                required: true,
                message: t('Please enter at least one section for this report')
              }]}
            >
              <Tree
                checkable
                defaultExpandedKeys={['sections']}
                defaultSelectedKeys={form.getFieldValue('sections')}
                switcherIcon={<ArrowDownIcon />}
                checkedKeys={['sections']}
                className="ant-tree-switcher-hidden"
                treeData={[
                  {
                    title: t('All Sections'),
                    key: 'sections',
                    selectable: false,
                    children: [
                      {
                        title: t(ANALYTICS_SECTIONS.SUMMARY.key, { context: 'analytics section' }),
                        selectable: false,
                        key: ANALYTICS_SECTIONS_KEYS.SUMMARY
                      },
                      {
                        title: t(ANALYTICS_SECTIONS.AUTOMATED_ANSWERS.key, { context: 'analytics section' }),
                        selectable: false,
                        key: ANALYTICS_SECTIONS_KEYS.AUTOMATED_ANSWERS
                      },
                      {
                        title: t(ANALYTICS_SECTIONS.LIVECHAT.key, { context: 'analytics section' }),
                        selectable: false,
                        key: ANALYTICS_SECTIONS_KEYS.LIVECHAT
                      },
                      {
                        title: t(ANALYTICS_SECTIONS.DECISION_TREES.key, { context: 'analytics section' }),
                        selectable: false,
                        key: ANALYTICS_SECTIONS_KEYS.DECISION_TREES
                      },
                      {
                        title: t(ANALYTICS_SECTIONS.INTERVIEWS.key, { context: 'analytics section' }),
                        selectable: false,
                        key: ANALYTICS_SECTIONS_KEYS.INTERVIEWS
                      },
                      {
                        title: t(ANALYTICS_SECTIONS.USER_FEEDBACK.key, { context: 'analytics section' }),
                        selectable: false,
                        key: ANALYTICS_SECTIONS_KEYS.USER_FEEDBACK
                      },
                      {
                        title: t(ANALYTICS_SECTIONS.MISSED_QUESTIONS.key, { context: 'analytics section' }),
                        selectable: false,
                        key: ANALYTICS_SECTIONS_KEYS.MISSED_QUESTIONS
                      },
                      {
                        title: t(ANALYTICS_SECTIONS.CSAT.key, { context: 'analytics section' }),
                        selectable: false,
                        key: ANALYTICS_SECTIONS_KEYS.CSAT
                      }
                    ]
                  }
                ]}
              />
            </Form.Item>
          </Col>
        </Row>

        <Divider />

        <Row wrap gutter={16}>
          <Col xs={formProps.mainColSpan} className="mb-5">
            <Typography.Paragraph className="mb-0">
              {t('Recipients')}
            </Typography.Paragraph>
            <Typography.Paragraph className="fs-sm mb-0" type="secondary" strong>
              {t('Add the report recipient(s)')}
            </Typography.Paragraph>
          </Col>

          <Col xs={formProps.secondaryColSpan} className="overflow-x-hidden">
            <Row wrap={false} align="bottom" className="mb-1">
              <Form.Item
                name="recipientInput"
                label={t('Recipient Email')}
                className="mb-0 mr-2 w-100"
              >
                <Input
                  placeholder={t('Type the email addresses here')}
                />
              </Form.Item>

              <Form.Item
                shouldUpdate
                noStyle
              >
                {() => (
                  <Button
                    type="primary"
                    onClick={onAddRecipients}
                  >
                    {t('Add')}
                  </Button>
                )}
              </Form.Item>
            </Row>

            <Form.Item
              shouldUpdate
              className="m-0"
              label={<Typography.Text className="fs-sm text-muted ml-3">{t('Seperate emails with commas')}</Typography.Text>}
            >
              {() => {
                if (!form.getFieldValue('recipients').length) {
                  return null;
                }

                return (
                  <List
                    className="overflow-y-auto overflow-x-hidden border-radius-base bg-layout px-4"
                    style={{ maxHeight: 180 }}
                  >
                    {
                      form.getFieldValue('recipients').map(recipient => (
                        <li key={recipient} className="my-3">
                          <Row gutter={16} wrap={false} align="middle">
                            <Col flex="auto">
                              <Typography.Text ellipsis>
                                {recipient}
                              </Typography.Text>
                            </Col>

                            <Col>
                              <Button
                                type="text"
                                size="small"
                                shape="circle"
                                icon={<CloseCircleFilled className="text-muted fs-sm" />}
                                onClick={() => form.setFieldsValue({
                                  recipients: form.getFieldValue('recipients').filter(email => email !== recipient)
                                })}
                              />
                            </Col>
                          </Row>
                        </li>
                      ))
                    }
                  </List>
                );
              }}
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </SideModal>
  );
};

ScheduledReportFormModal.propTypes = {
  selectedItem: PropTypes.object,
  setSelectedItem: PropTypes.func
};

export default ScheduledReportFormModal;
